import {
  subYears,
  subDays,
  subMonths,
  subWeeks,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfQuarter,
  endOfQuarter,
  addDays,
  format,
  isSameDay,
  differenceInYears,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  addMonths,
  addYears,
  parseISO
} from 'date-fns'
import {DateRange} from "react-day-picker";

import {DateShortcut} from "@/lib/filters/date/types";

export function getNow() {
  return new Date()
}

export function getDateRangeStart() {
  return new Date('2014-01-01')
}

export function getDaysPlus(days: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return addDays(referenceDate, days)
}
export function getDaysAgo(days: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return subDays(referenceDate, days)
}

export function getWeeksAgo(weeks: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return subWeeks(referenceDate, weeks)
}

export function getMonthsAgo(months: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return subMonths(referenceDate, months)
}

export function getMonthsPlus(months: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return addMonths(referenceDate, months)
}

export function getYearsAgo(years: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return subYears(referenceDate, years)
}

export function getYearsPlus(years: number, referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return addYears(referenceDate, years)
}

export function getStartOfWeek(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return startOfWeek(referenceDate, {weekStartsOn: 1})
}

export function getEndOfWeek(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return endOfWeek(referenceDate, {weekStartsOn: 1})
}

export function getStartOfMonth(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return startOfMonth(referenceDate)
}

export function getEndOfMonth(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return endOfMonth(referenceDate)
}

export function getStartOfQuarter(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return startOfQuarter(referenceDate)
}

export function getEndOfQuarter(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return endOfQuarter(referenceDate)
}

export function getStartOfYear(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return startOfYear(referenceDate)
}

export function getEndOfYear(referenceDate: Date | undefined): Date | undefined {
  if (!referenceDate) {
    return
  }

  return endOfYear(referenceDate)
}

export function formatDate(referenceDate: Date | undefined, formatString: string): string | undefined {
  if (!referenceDate) {
    return
  }

  return format(referenceDate, formatString)
}

export function getRangeSelectShortcutButtons(): DateShortcut[] {
  const now = getNow();
  return [
    {
      label: "Last 7 Days",
      date: {
        from: getDaysAgo(6, now),
        to: now
      }
    },
    {
      label: "Last 30 Days",
      date: {
        from: getDaysPlus(1, getMonthsAgo(1, now)),
        to: now
      }
    },
    {
      label: "Last 3 Months",
      date: {
        from: getDaysPlus(1, getMonthsAgo(3, now)),
        to: now
      }
    },
    {
      label: "Last 12 Months",
      date: {
        from: getDaysPlus(1, getYearsAgo(1, now)),
        to: now
      }
    },
    {
      label: "Week to Date",
      date: {
        from: getStartOfWeek(now),
        to: now
      },
    },
    {
      label: "Month to Date",
      date: {
        from: getStartOfMonth(now),
        to: now
      }
    },
    {
      label: "Quarter to Date",
      date: {
        from: getStartOfQuarter(now),
        to: now
      }
    },
    {
      label: "Year to Date",
      date: {
        from: getStartOfYear(now),
        to: now
      }
    },
    {
      label: "All time",
      date: {
        from: getDateRangeStart(),
        to: now
      }
    }
  ];
}
export function getRangeSelectShortcutButtonsShortList(): DateShortcut[] {
  const now = getNow()
  return [
    {
      label: "12 Months",
      date: {
        from: getDaysPlus(1, getYearsAgo(1, now)),
        to: now
      }
    },
    {
      label: "6 Months",
      date: {
        from: getDaysPlus(1, getMonthsAgo(6, now)),
        to: now
      }
    },
    {
      label: "3 Months",
      date: {
        from: getDaysPlus(1, getMonthsAgo(3, now)),
        to: now
      }
    },
    {
      label: "1 Month",
      date: {
        from: getDaysPlus(1, getMonthsAgo(1, now)),
        to: now
      }
    }
  ];
}

export function areDateRangesEqual(firstDate: DateRange | undefined, secondDate: DateRange | undefined): boolean {
  if (
    !firstDate || !secondDate ||
    !firstDate.to || !secondDate.to ||
    !firstDate.from || !secondDate.from
  ) {
    return false
  }

  return isSameDay(firstDate.to, secondDate.to) && isSameDay(firstDate.from, secondDate.from)
}

export function areDatesEqual(firstDate: Date | undefined, secondDate: Date | undefined): boolean {
  if (!firstDate || !secondDate) {
    return false
  }

  return isSameDay(firstDate, secondDate)
}

export function dateDiffInYears(firstDate: Date | undefined, secondDate: Date | undefined): number | false {
  if (!(firstDate && secondDate)) {
    return false
  }

  return differenceInYears(firstDate, secondDate)
}

export function isFullMonth(date: DateRange | undefined): boolean {
  if (!date || !date.to || !date.from) {
    return false
  }

  return isFirstDayOfTheMonth(date.from) && isLastDayOfTheMonth(date.to);
}

export function isLastDayOfTheMonth(date: Date | undefined): boolean {
  if (!date) {
    return false
  }

  return isLastDayOfMonth(date);
}
export function isFirstDayOfTheMonth(date: Date | undefined): boolean {
  if (!date) {
    return false
  }

  return isFirstDayOfMonth(date);
}

export const getDateFromString = (str: string): string => {
  const date = parseISO(str);

  return format(date, 'dd/MM/yyyy');
}

export const getHumanReadableMonthYearFromString = (str: string): string => {
  const date = parseISO(str);
  return format(date, 'MMM yyyy');
}

export const getDateTimeFromString = (str: string): string => {
  const date = parseISO(str);

  return format(date, 'dd/MM/yyyy hh:mm:ss');
}

export const getGuideYear = () => {
  return 2027;
}

export const getCurrentYear = () => {
  return 2026;
}